import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button, Card, Badge } from 'react-bootstrap';
import Header1 from '../components/Header1';
import {
  FaUsers,
  FaHeartbeat,
  FaRegCheckCircle,
  FaLightbulb,
  FaHandshake,
  FaBriefcase,
} from 'react-icons/fa';
import { motion } from 'framer-motion';

// Import your images (make sure to add them to your project)
import teamImage from '../assets/teamImage.jpg';
import perksImage from '../assets/perksImage.jpg';
import openingsImage from '../assets/openingsImage.jpg';
import hiringImage from '../assets/hiringImage.jpg';
import careerHero from '../assets/careerHero.jpg';

import { useState, useEffect } from 'react';
import api from '../api';

export default function CareerPage() {
  const [googleFormLink, setGoogleFormLink] = useState('https://forms.gle/GxkRyfnrXfD9Jo7n6');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/api/settings');
        if (data && data.career_google_form_url) {
          setGoogleFormLink(data.career_google_form_url);
        }
      } catch (err) {
        console.error('Error fetching site settings', err);
      }
    };
    fetchSettings();
  }, []);

  const styles = {
    header: {
      fontSize: '2rem',
      color: '#801001',
      fontWeight: '700',
    },
    subHeader: {
      color: '#801001',
      fontWeight: '600',
    },
    contactCard: {
      borderRadius: '15px',
      background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
      border: 'none',
      boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    applyButton: {
      background: 'linear-gradient(135deg, #801001, #b81414)',
      border: 'none',
      fontWeight: '600',
      borderRadius: '50px',
      fontSize: '1.1rem',
      padding: '12px 32px',
      boxShadow: '0 4px 15px rgba(128, 16, 1, 0.3)',
    },
    applyButtonHover: {
      background: 'linear-gradient(135deg, #5c0b00, #801001)',
      transform: 'translateY(-2px)',
    },
    perkIcon: {
      color: '#801001',
      fontSize: '1.5rem',
      marginRight: '10px',
    },
    imageStyle: {
      borderRadius: '10px',
      objectFit: 'cover',
      width: '100%',
      height: '300px',
      marginBottom: '20px',
    },
    heroImage: {
      borderRadius: '10px',
      objectFit: 'cover',
      width: '100%',
      maxHeight: '400px',
      marginBottom: '20px',
    },
  };

  const cardHover = {
    hover: {
      y: -5,
      boxShadow: '0 15px 30px rgba(0,0,0,0.12)',
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div style={{ background: '#f9f9f9', minHeight: '100vh' }}>
      <Header1 />
      <Container className="py-5">
        <Helmet>
          <title>Careers</title>
        </Helmet>

        <Row className="justify-content-center">
          <Col lg={10}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Card
                className="p-5 text-center mb-5 border-0"
                style={styles.contactCard}
              >
                <img
                  src={careerHero}
                  alt="Team working together"
                  style={styles.heroImage}
                />
                <div className="mb-4">
                  <h1 className="mb-3" style={styles.header}>
                    Build Your Future With Us
                  </h1>
                  <p className="lead mb-4" style={{ fontSize: '1.25rem' }}>
                    Step into a world where your <strong>career</strong> goes
                    beyond a job. Become part of a<strong> dynamic team</strong>{' '}
                    driving <strong>innovation</strong> in the garment industry
                    and create a<strong> lasting impact</strong> with every
                    stitch.
                  </p>

                  <motion.div whileHover={{ scale: 1.05 }}>
                    <Button
                      href={googleFormLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.applyButton}
                      className="mt-3"
                    >
                      Explore Opportunities
                    </Button>
                  </motion.div>
                </div>
              </Card>
            </motion.div>

            <Row className="g-4 mb-5 align-items-stretch">
              <Col md={6} className="d-flex">
                <motion.div
                  whileHover="hover"
                  variants={cardHover}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="d-flex w-100"
                >
                  <Card
                    className="h-100 p-4 d-flex flex-column"
                    style={styles.contactCard}
                  >
                    <img
                      src={teamImage}
                      alt="Our team culture"
                      style={styles.imageStyle}
                    />
                    <h4 style={styles.subHeader}>
                      <FaUsers className="me-2" style={styles.perkIcon} />
                      Our Culture
                    </h4>
                    <div className="mt-3 flex-grow-1">
                      <p>
                        At the heart of our company is a culture built on{' '}
                        <strong>collaboration</strong>,{' '}
                        <strong>innovation</strong>, and{' '}
                        <strong>respect</strong>. We believe in empowering every
                        team member to bring their <strong>unique ideas</strong>{' '}
                        to the table, fostering a supportive environment where{' '}
                        <strong>creativity thrives</strong>. Together, we
                        embrace <strong>challenges</strong>, celebrate{' '}
                        <strong>successes</strong>, and continuously strive to{' '}
                        <strong>redefine the garment industry</strong>.
                      </p>
                    </div>
                  </Card>
                </motion.div>
              </Col>

              <Col md={6} className="d-flex">
                <motion.div
                  whileHover="hover"
                  variants={cardHover}
                  initial="hidden"
                  animate="visible"
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="d-flex w-100"
                >
                  <Card
                    className="h-100 p-4 d-flex flex-column"
                    style={styles.contactCard}
                  >
                    <img
                      src={perksImage}
                      alt="Employee perks and benefits"
                      style={styles.imageStyle}
                    />
                    <h4 style={styles.subHeader}>
                      <FaHeartbeat className="me-2" style={styles.perkIcon} />
                      Perks & Benefits
                    </h4>
                    <div className="mt-3 flex-grow-1 d-flex flex-column justify-content-between">
                      <p>
                        From professional development to personal wellness, we
                        provide benefits that matter
                      </p>
                      <ul className="mt-3">
                        <li>🎓 Skill Development Workshops & Training</li>
                        <li>🎉 Team Events</li>
                        <li>🏞️ Team Outings</li>
                        <li>💰 Competitive Salary</li>
                        <li>🏆 Performance Bonuses</li>
                        <li>🌟 Recognition Programs</li>
                        <li>🖥️ Adaptable Work Options</li>
                      </ul>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            </Row>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card className="p-4 mb-5" style={styles.contactCard}>
                <img
                  src={openingsImage}
                  alt="Current job openings"
                  style={styles.imageStyle}
                />
                <h4 style={styles.subHeader}>
                  <FaBriefcase className="me-2" style={styles.perkIcon} />
                  Career Opportunities
                </h4>
                <p className="mt-3">
                  Passion fuels our work—and we’re looking for people who share
                  it. Explore the opportunities below:
                </p>

                <div className="d-flex flex-wrap my-4">
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Fashion Designer
                  </Badge>
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Production Supervisor{' '}
                  </Badge>
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Quality Control Inspector
                  </Badge>
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Pattern Maker
                  </Badge>
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Inventory Manager
                  </Badge>
                  <Badge className="bg-secondary text-light me-2 mb-2">
                    Sewing Machine Operator
                  </Badge>
                </div>

                <p>
                  <FaLightbulb className="me-2" style={{ color: '#ffc107' }} />
                  <strong>Don’t see a role that matches you? </strong> We’re
                  always open to connecting with exceptional talent—share how
                  you can make an impact with us.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card className="p-4 mb-4" style={styles.contactCard}>
                <img
                  src={hiringImage}
                  alt="Our hiring process"
                  style={styles.imageStyle}
                />
                <h4 style={styles.subHeader}>
                  <FaRegCheckCircle className="me-2" style={styles.perkIcon} />
                  Hiring Journey
                </h4>
                <p className="mt-3">
                  We keep our process transparent and respectful of your time:
                </p>

                <div className="timeline mt-4">
                  <div className="timeline-step">
                    <div
                      className="timeline-icon"
                      style={{ backgroundColor: '#f8e9e9', color: '#801001' }}
                    >
                      1
                    </div>
                    <h6>Application Review</h6>
                    <p>
                      Every application is thoughtfully reviewed to identify the
                      best fit for our team.
                    </p>
                  </div>

                  <div className="timeline-step">
                    <div
                      className="timeline-icon"
                      style={{ backgroundColor: '#f8e9e9', color: '#801001' }}
                    >
                      2
                    </div>
                    <h6>Preliminary Call</h6>
                    <p>
                      A friendly conversation to get to know you better and
                      learn about your aspirations.
                    </p>
                  </div>

                  <div className="timeline-step">
                    <div
                      className="timeline-icon"
                      style={{ backgroundColor: '#f8e9e9', color: '#801001' }}
                    >
                      3
                    </div>
                    <h6>Skills Assessment</h6>
                    <p>
                      A role-related assessment to better understand your
                      strengths.
                    </p>
                  </div>

                  <div className="timeline-step">
                    <div
                      className="timeline-icon"
                      style={{ backgroundColor: '#f8e9e9', color: '#801001' }}
                    >
                      4
                    </div>
                    <h6>Team Interview</h6>
                    <p>
                      An opportunity to connect with future colleagues and
                      experience our culture
                    </p>
                  </div>

                  <div className="timeline-step">
                    <div
                      className="timeline-icon"
                      style={{ backgroundColor: '#f8e9e9', color: '#801001' }}
                    >
                      5
                    </div>
                    <h6>Hiring Proposal </h6>
                    <p>
                      We’ll share your offer details and get you ready for the
                      next chapter with us
                    </p>
                  </div>
                </div>

                <div className="mt-4 text-center">
                  <FaHandshake
                    className="me-2"
                    style={{ color: '#801001', fontSize: '1.5rem' }}
                  />
                  <h5 className="d-inline">
                    Ready to start your journey with us?
                  </h5>
                  <motion.div whileHover={{ scale: 1.05 }} className="mt-3">
                    <Button
                      href={googleFormLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.applyButton}
                    >
                      Apply Now
                    </Button>
                  </motion.div>
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>

      <style>{`
  .timeline {
    position: relative;
    padding-left: 50px;
  }
  .timeline-step {
    position: relative;
    padding-bottom: 30px;
  }
  .timeline-step:last-child {
    padding-bottom: 0;
  }
  .timeline-icon {
    position: absolute;
    left: -50px;
    top: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
  }
  .timeline-step h6 {
    font-weight: 600;
    color: #801001;
    margin-bottom: 5px;
  }
  .timeline-step p {
    color: #666;
    margin-bottom: 0;
  }
  .timeline-step:not(:last-child):after {
    content: '';
    position: absolute;
    left: -32px;
    top: 36px;
    height: calc(100% - 36px);
    width: 2px;
    background: #e9e9e9;
  }
`}</style>
    </div>
  );
}
